import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarDays, Stethoscope, UserRound } from 'lucide-react';
import type { AvailabilitySlot, PublicDoctor } from '@his/shared';
import { publicGet, publicPost } from '@/lib/public-api';
import { qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { timeOnly, todayIso } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { EmptyState } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, FormSkeleton, Input, Textarea } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { usePublicAuth } from './PublicLayout';
import { usePrefill } from './use-prefill';

export function RequestAppointmentPage() {
  const { slug = '', branchId = '' } = useParams();
  const [params] = useSearchParams();
  const doctorId = params.get('doctorId') ?? '';
  const returnTo = safeReturnTo(params.get('returnTo'), `/find/${slug}/${branchId}?tab=doctors`);

  const doctors = useQuery({
    queryKey: ['public-doctors', slug, branchId],
    queryFn: () => publicGet<PublicDoctor[]>(`/facilities/${slug}/branches/${branchId}/doctors`),
  });

  if (doctors.isLoading) return <FormSkeleton />;
  const doctor = doctors.data?.find((candidate) => candidate.id === doctorId);
  if (!doctor) return <EmptyState title="Doctor not found" description="This doctor is no longer listed at this branch." />;

  return <RequestAppointmentForm key={doctor.id} slug={slug} branchId={branchId} doctor={doctor} returnTo={returnTo} />;
}

function RequestAppointmentForm({
  slug,
  branchId,
  doctor,
  returnTo,
}: {
  slug: string;
  branchId: string;
  doctor: PublicDoctor;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('portal');
  const { account, requireSignIn } = usePublicAuth();
  const [initialDate] = useState(todayIso());
  const [date, setDate] = useState(initialDate);
  const [slotAt, setSlotAt] = useState('');
  const [patientName, setPatientName] = useState(account?.name ?? '');
  const [phone, setPhone] = useState(account?.phone ?? '');
  const [sex, setSex] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  usePrefill(patientName, setPatientName, account?.name);
  usePrefill(phone, setPhone, account?.phone);

  const availability = useQuery({
    queryKey: ['public-availability', slug, branchId, doctor.id, date],
    queryFn: () =>
      publicGet<AvailabilitySlot[]>(
        `/facilities/${slug}/branches/${branchId}/availability${qs({ doctorId: doctor.id, date })}`,
      ),
    enabled: Boolean(date),
  });

  const send = useMutation({
    mutationFn: () =>
      publicPost('/requests', {
        tenantSlug: slug,
        branchId,
        kind: 'opd',
        patientName,
        phone,
        sex: sex || undefined,
        doctorId: doctor.id,
        requestedSlotAt: slotAt || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Request sent', 'The hospital will confirm your appointment.');
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(patientName.trim() && phone.trim());
  const dirty =
    !send.isSuccess &&
    (date !== initialDate ||
      slotAt !== '' ||
      sex !== '' ||
      notes !== '' ||
      patientName !== (account?.name ?? '') ||
      phone !== (account?.phone ?? ''));

  return (
    <FormScreen
      title={`Request an appointment with ${doctor.name}`}
      description="The hospital confirms the time before it is booked"
      icon={Stethoscope}
      eyebrow={t('screen.find_care')}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!account) {
          requireSignIn();
          return;
        }
        if (!ready) return;
        setError(null);
        send.mutate();
      }}
    >
      <FormSection title={t('section.when')} icon={CalendarDays}>
        <Field label="Preferred date">
          <DatePicker
            value={date}
            onChange={(value) => {
              setDate(value);
              setSlotAt('');
            }}
          />
        </Field>
        <Field
          label="Preferred time"
          className="sm:col-span-2"
          hint={
            availability.data?.length === 0
              ? 'No clinic on this day — pick another date, or send the request without a time.'
              : undefined
          }
        >
          {availability.isFetching ? (
            <p className="text-sm text-muted-foreground">Checking…</p>
          ) : (
            <div className="grid max-h-40 grid-cols-3 gap-1.5 overflow-y-auto rounded-md border p-2 sm:grid-cols-4">
              {availability.data?.map((slot) => (
                <button
                  key={slot.startsAt}
                  type="button"
                  disabled={!slot.available}
                  onClick={() => setSlotAt(slot.startsAt)}
                  className={`rounded px-2 py-1 text-xs tabular transition-colors focus-ring ${
                    slotAt === slot.startsAt
                      ? 'bg-primary text-primary-foreground'
                      : slot.available
                        ? 'bg-muted hover:bg-accent'
                        : 'cursor-not-allowed bg-muted/40 text-muted-foreground line-through'
                  }`}
                >
                  {timeOnly(slot.startsAt)}
                </button>
              ))}
              {availability.data?.length === 0 && (
                <p className="col-span-full py-3 text-center text-sm text-muted-foreground">
                  No clinic on this day.
                </p>
              )}
            </div>
          )}
        </Field>
      </FormSection>

      <FormSection title={t('section.who')} icon={UserRound}>
        <Field label="Your name" required>
          <Input value={patientName} onChange={(e) => setPatientName(e.target.value)} />
        </Field>
        <Field label="Phone" required>
          <PhoneText value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
        </Field>
        <Field label="Sex">
          <ChoiceButtons
            layout="chips"
            value={sex}
            onChange={setSex}
            options={[
              { value: '', label: 'Prefer not to say' },
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </Field>
        <Field label="What is the problem?" className="sm:col-span-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={account ? 'Send request' : 'Sign in to continue'}
        loading={send.isPending}
        incomplete={Boolean(account) && !ready}
        cancelTo={returnTo}
        bleed={false}
      />
    </FormScreen>
  );
}
